import React from "react";
import { useDispatch } from "react-redux";
import { actualizarMensaje, mostrarModulo } from "../../../../redux/actions/actGlobal";
import {
  consulta,
  obtenerPermisosValorAlt,
  obtenerValores,
  obtenerValoresFiltros,
} from "../../../../global/js/funciones";
import { PREGUNTAS, RESPUESTAS } from "../helper";

// helpers idénticos a tu archivo original
const isTempQuestionId = (id) =>
  typeof id === 'string' && (id.startsWith('qtmp_') || Number.isNaN(Number(id)));

const isTempOptionId = (id) =>
  typeof id === 'string' && String(id).startsWith('tmp_');

const asNumberOrNull = (v) => {
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const newTmpQId = () => `qtmp_${Math.random().toString(36).slice(2, 9)}`;
const newTmpId = () => `tmp_${Math.random().toString(36).slice(2, 9)}`;

export default function useEncuestaGenero() {
  const dispatchI = useDispatch();

  // datos
  const [preguntas, setPreguntas] = React.useState([]);
  const [periodo, setPeriodo] = React.useState({});
  const [respuestas, setRespuestas] = React.useState([]);
  const [preguntaEditando, setPreguntaEditando] = React.useState(null);

  // estado
  const [loading, setLoading] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [fromEdit, setFromEdit] = React.useState(false);

  const [selectedId, setSelectedId] = React.useState(null);

  React.useEffect(() => {
    dispatchI(
      mostrarModulo({
        ruta: "/gestion_encuesta_genero",
        nombre: "Gestión de Encuesta de Género",
      })
    );
    obtenerDatosIniciales();
  }, []);

  const obtenerDatosIniciales = async () => {
    setLoading(true);
    try {
      const data = await obtenerValoresFiltros([
        { llave: "generica", valor: PREGUNTAS },
        { llave: "valorc", valor: "1" },
        { llave: "estado", valor: "1" },
      ]);
      const periodoRes = await obtenerValoresFiltros([
        { llave: "codigo", valor: "per_enc_gen" },
      ]);
      setPreguntas(Array.isArray(data) ? data : []);
      setPeriodo(periodoRes?.[0] || {});
    } catch (err) {
      console.error("Error cargando datos iniciales:", err);
    } finally {
      setLoading(false);
    }
  };

  const obtenerDatosAll = async (id) => {
    setLoading(true);
    try {
      const data = await obtenerValoresFiltros([
        { llave: "generica", valor: PREGUNTAS },
        { llave: "valorc", valor: "1" },
        { llave: "estado", valor: "1" },
      ]);
      const resp = await obtenerPermisosValorAlt([
        { llave: "principal", valor: id },
        { llave: "estado", valor: "1" },
        { llave: "secundario__generica", valor: RESPUESTAS },
        { llave: "secundario__valorc", valor: "1" },
      ]);
      setRespuestas(Array.isArray(resp) ? resp : []);
      setPreguntas(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error cargando datos iniciales:", err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!selectedId) return;

    if (isTempQuestionId(selectedId)) {
      setRespuestas([]);
      setPreguntaEditando(prev =>
        prev && prev.id === selectedId
          ? { ...prev, respuestas: prev.respuestas || [] }
          : prev
      );
      setFromEdit(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const resp = await obtenerPermisosValorAlt([
          { llave: 'principal', valor: selectedId },
          { llave: 'estado', valor: '1' },
          { llave: 'secundario__generica', valor: RESPUESTAS },
          { llave: "secundario__valorc", valor: "1" },
        ]);
        console.log('Respuestas obtenidas:', resp);

        if (cancelled) return;

        setRespuestas(Array.isArray(resp) ? resp : []);

        setPreguntaEditando(prev => {
          if (!prev || prev.id !== selectedId) return prev;
          if (prev.respuestas && prev.respuestas.length) return prev;
          const respuestasx = (resp || []).map(r => ({
            id: r.id,
            secundario: { id: r.secundario?.id ?? null, nombre: r.secundario?.nombre ?? '' },
          }));
          return { ...prev, respuestas: respuestasx };
        });
      } catch (e) {
        console.error('Error al obtener respuestas:', e);
        if (!cancelled) setRespuestas([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setFromEdit(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [selectedId]);

  const addQuestion = React.useCallback(() => {
    const nextOrden = (
      preguntas?.length
        ? Math.max(
          ...preguntas.map((p) => Number(p.valorb)).filter((n) => !Number.isNaN(n))
        ) + 1
        : 1
    ).toString();

    const tmpId = newTmpQId();

    const nuevaPreguntaLista = {
      id: tmpId,
      nombre: "Nueva pregunta",
      valora: "",
      valorb: nextOrden,
      __state: "new",
    };
    setPreguntas((prev) => [...(prev || []), nuevaPreguntaLista]);

    const nuevaPreguntaEditor = {
      id: tmpId,
      nombre: "Nueva pregunta",
      valora: "",
      valorb: nextOrden,
      respuestas: [],
      __state: "new",
      __changed: { nombre: true, valorb: true },
    };
    setPreguntaEditando(nuevaPreguntaEditor);
    setIsEditing(true);
    setFromEdit(true);
    setSelectedId(tmpId);
  }, [preguntas]);

  const handleEditarPregunta = (q) => {
    const respuestasExistentes =
      Array.isArray(respuestas) && String(selectedId) === String(q.id)
        ? (respuestas || []).map((r) => ({
          id: r.id,
          secundario: {
            id: r.secundario?.id ?? null,
            nombre: r.secundario?.nombre ?? "",
          },
        }))
        : [];

    setPreguntaEditando({
      id: q.id,
      nombre: q.nombre,
      valora: q.valora,
      valorb: q.valorb,
      respuestas: respuestasExistentes,
      __changed: {},
    });

    setIsEditing(true);
    setFromEdit(true);

    setSelectedId((prev) => (prev === q.id ? prev : q.id));
  };

  const onChangeCampoPregunta = (field, value) => {
    setPreguntaEditando((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: value,
        __changed: { ...(prev.__changed || {}), [field]: true },
      };
    });
  };

  const updateOption = React.useCallback((qid, oid, nombre) => {
    setPreguntaEditando((prev) => {
      if (!prev || prev.id !== qid) return prev;
      const next = (prev.respuestas || []).map((o) =>
        o.id === oid
          ? {
            ...o,
            secundario: { ...(o.secundario || {}), nombre },
            __state: o.__state === "new" ? "new" : "edited",
          }
          : o
      );
      return { ...prev, respuestas: next };
    });
  }, []);

  const deleteOption = React.useCallback((qid, oid) => {
    setPreguntaEditando((prev) => {
      if (!prev || prev.id !== qid) return prev;

      const opciones = prev.respuestas || [];
      const target = opciones.find((o) => o.id === oid);
      if (!target) return prev;

      const isLocal =
        target.__state === "new" ||
        String(oid).startsWith("tmp_") ||
        target?.secundario?.id == null;

      const next = isLocal
        ? opciones.filter((o) => o.id !== oid)
        : opciones.map((o) =>
          o.id === oid ? { ...o, __state: "deleted" } : o
        );

      return { ...prev, respuestas: next };
    });
  }, []);

  const addOption = React.useCallback((qid) => {
    setPreguntaEditando((prev) => {
      if (!prev || prev.id !== qid) return prev;
      const nueva = {
        id: newTmpId(),
        secundario: { id: null, nombre: "Nueva opción" },
        __state: "new",
      };
      return { ...prev, respuestas: [...(prev.respuestas || []), nueva] };
    });
  }, []);

  const getPreguntaCambios = (p) => ({
    nombre: !!p?.__changed?.nombre,
    valora: !!p?.__changed?.valora,
    valorb: !!p?.__changed?.valorb,
  });

  const buildSavePayload = (p) => {
    if (!p) return null;

    const es_nueva = isTempQuestionId(p.id) || p.id == null || p.__state === 'new';

    const pregunta = {
      id: es_nueva ? null : p.id,
      nombre: (p.nombre || '').trim(),
      valora: asNumberOrNull(p.valora), // 1 texto, 2 selección, 3 tratamiento
      valorb: asNumberOrNull(p.valorb),
    };

    const cambios_pregunta = getPreguntaCambios(p);

    if (String(p.valora) === '1') {
      return { es_nueva, pregunta, cambios_pregunta, opciones: { crear: [], actualizar: [], eliminar: [] } };
    }

    const crear = [];
    const actualizar = [];
    const eliminar = [];

    (p.respuestas || []).forEach((o) => {
      const nombre = o?.secundario?.nombre?.trim() ?? '';
      if (o.__state === 'new') {
        if (nombre) crear.push({ nombre });
        return;
      }
      if (o.__state === 'edited') {
        if (o?.secundario?.id && nombre) actualizar.push({ id: o.secundario.id, nombre });
        return;
      }
      if (o.__state === 'deleted') {
        if (o?.secundario?.id) eliminar.push({ id: o.secundario.id });
        return;
      }
    });

    return { es_nueva, pregunta, cambios_pregunta, opciones: { crear, actualizar, eliminar } };
  };

  const guardarPregunta = React.useCallback(() => {
    if (!preguntaEditando) return;

    const payload = buildSavePayload(preguntaEditando);
    if (!payload) return;

    if (!payload.pregunta.nombre) {
      dispatchI(
        actualizarMensaje({
          titulo: "El título (nombre) es obligatorio.",
          tipo: "warning",
          mostrar: true,
          tiempo: 3000,
        })
      );
      return;
    }
    if (![1, 2, 3].includes(payload.pregunta.valora)) {
      dispatchI(
        actualizarMensaje({
          titulo: "Selecciona un tipo de respuesta válido.",
          tipo: "warning",
          mostrar: true,
          tiempo: 3000,
        })
      );
      return;
    }

    const ordenNum = payload.pregunta.valorb;
    if (ordenNum == null || Number.isNaN(Number(ordenNum))) {
      dispatchI(
        actualizarMensaje({
          titulo: "Indica el número de orden (debe ser numérico).",
          tipo: "warning",
          mostrar: true,
          tiempo: 3000,
        })
      );
      return;
    }

    const existeMismoOrden = (preguntas || []).some((q) => {
      const mismoNumero = Number(q.valorb) === Number(ordenNum);
      const distintaPregunta = String(q.id) !== String(preguntaEditando.id);
      return mismoNumero && distintaPregunta;
    });

    if (existeMismoOrden) {
      // const nuevo = sugerirOrdenLibre();
      // onChangeCampoPregunta("valorb", String(nuevo));

      dispatchI(
        actualizarMensaje({
          titulo: `El número de orden ${ordenNum} ya existe. Asigna otro diferente.`,
          tipo: "info",
          mostrar: true,
          tiempo: 4000,
        })
      );
      return;
    }

    setLoading(true);
    consulta(
      "api/v1.0/personas/encuesta-genero/pregunta/guardar",
      payload,
      "post",
      (error, estado, resp) => {
        setLoading(false);

        if (estado === 200) {
          const newId = resp?.id ?? payload.pregunta.id ?? selectedId;

          dispatchI(
            actualizarMensaje({
              titulo: "Pregunta guardada correctamente.",
              tipo: "success",
              mostrar: true,
              tiempo: 2500,
            })
          );

          obtenerDatosAll(newId);
          setIsEditing(false);
          setPreguntaEditando(null);
        } else {
          dispatchI(
            actualizarMensaje({
              titulo: "No se pudo guardar. Verifica e intenta de nuevo.",
              tipo: "warning",
              mostrar: true,
              tiempo: 4000,
            })
          );
          console.error("Guardar pregunta - error:", error, resp);
        }
      }
    );
  }, [preguntaEditando, selectedId, preguntas]);


  const eliminarPregunta = (id) => {
    if (isTempQuestionId(id)) {
      setPreguntas((prev) =>
        (prev || []).filter((q) => String(q.id) !== String(id))
      );
      if (String(selectedId) === String(id)) {
        setSelectedId(null);
        setRespuestas([]);
        setIsEditing(false);
        setPreguntaEditando(null);
      }
      return;
    }

    setLoading(true);
    consulta(
      `api/v1.0/personas/encuesta-genero/pregunta/${id}/eliminar`,
      null,
      "delete",
      (error, estado, resp) => {
        if (estado === 200) {
          obtenerDatosIniciales();
          setLoading(false);
        } else {
          dispatchI(
            actualizarMensaje({
              titulo: "Ha ocurrido un error, contacte con el administrador.",
              tipo: "warning",
              mostrar: true,
              tiempo: 4000,
            })
          );
          setLoading(false);
        }
      }
    );
  };

  const hasCambios = React.useMemo(() => {
    const p = preguntaEditando;
    if (!p) return false;
    if (p.__state === 'new') return true;
    if (p.__changed && Object.values(p.__changed).some(Boolean)) return true;
    if (Array.isArray(p.respuestas) && p.respuestas.some(o =>
      o && ['new', 'edited', 'deleted'].includes(o.__state)
    )) return true;
    return false;
  }, [preguntaEditando]);

  const limpiarEdicion = React.useCallback(() => {
    setIsEditing(false);
    setPreguntaEditando(null);
  }, []);


  const selected = React.useMemo(
    () => (preguntas || []).find((q) => q.id === selectedId) || null,
    [preguntas, selectedId]
  );

  return {
    // estado expuesto
    loading, preguntas, periodo, respuestas,
    isEditing, setIsEditing,
    selectedId, setSelectedId,
    selected,
    preguntaEditando, setPreguntaEditando,
    hasCambios,

    // acciones
    addQuestion,
    handleEditarPregunta,
    onChangeCampoPregunta,
    updateOption,
    deleteOption,
    addOption,
    guardarPregunta,
    eliminarPregunta,
    limpiarEdicion,
  };

}
